import { DvCheckboxCellRenderer } from '@devinco/ui/grid';
import {
  AfterViewInit,
  Directive,
  EventEmitter,
  HostListener,
  Input,
  Output,
  inject,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { RestApiService } from '@core/services/rest-api.service';
import { DvFormUtils } from '@devinco/ui/legacy';
import { GridContext } from '@features/vt-grid/interfaces/grid-context';
import RowEditUtils from '@features/vt-grid/utils/row-edit.utils';
import { Entity } from '@shared/constants/entity';
import { HttpErrorModel } from '@shared/interfaces/http-error.model';
import { AgGridAngular } from 'ag-grid-angular';
import {
  CellKeyDownEvent,
  CellPosition,
  Column,
  GridApi,
  IRowNode,
  RowEditingStartedEvent,
  TabToNextCellParams,
} from 'ag-grid-community';
import { BehaviorSubject, Observable, catchError, map, of } from 'rxjs';

@Directive({
  selector: 'ag-grid-angular[vtGridRowEditing]',
  standalone: false,
})
export class GridRowEditingDirective implements AfterViewInit {
  private agGrid = inject(AgGridAngular);
  private api = inject(RestApiService);

  @Input() entity?: Entity;
  @Input() entityId?: string;
  /** replaces default upsert by entity/entityId, emitted value is merged into row data */
  @Input() upsertFn?: (
    node: IRowNode,
    dirtyValues: Record<string, unknown>
  ) => Observable<object | void>;
  @Output() inlineEditUpdateEnd = new EventEmitter<GridApi>();

  private inlineEditingInProgress: boolean;

  ngAfterViewInit(): void {
    if (this.agGrid.gridOptions) {
      this.agGrid.api.setGridOption('defaultColDef', {
        ...this.agGrid.api.getGridOption('defaultColDef'),
      });
      this.agGrid.tabToNextCell = this.tabToNextCell;
      this.agGrid.gridOptions.context.formGroup = RowEditUtils.createFormGroup(
        this.agGrid.api
          .getColumns()
          ?.filter(col => !!col.getColDef().cellEditor)
          .map(column => column.getColDef()) || []
      );

      this.agGrid.gridOptions.context.isInlineEditingInProgress$ =
        new BehaviorSubject<boolean>(false);
      this.agGrid.api.setGridOption('editType', 'fullRow');
    }
  }

  @HostListener('cellKeyDown', ['$event'])
  onCellKeyDown(evt: Event): void {
    const event = evt as unknown as CellKeyDownEvent;
    const focusedColDef = event.api.getFocusedCell()?.column.getColDef();
    const selectedField = focusedColDef?.field;

    if (!focusedColDef || !selectedField) return;
    // sets value on editable checkbox when clicking space when inline editing is not happening
    if ((event.event as unknown as { key: string }).key === ' ') {
      if (
        event.colDef.cellRenderer === DvCheckboxCellRenderer &&
        !event.colDef.cellRendererParams.readonly &&
        !event.colDef.cellRendererParams?.isDisabled?.({ data: event.data }) &&
        !event.api.getEditingCells().length &&
        event.column.isCellEditable(event.node)
      ) {
        event.node.setDataValue(selectedField, !event.data[selectedField]);
      }
    }
  }

  @HostListener('rowEditingStarted', ['$event'])
  rowEditingStarted(evt: Event): void {
    const event = evt as unknown as RowEditingStartedEvent;
    // cancels inline editing and clears formGroup in case shift + doubleClick is used in inline editing
    if (this.inlineEditingInProgress) {
      event.api.stopEditing(true);
      this.enableFormGroup(event.context);
      const rowIndex = event.node.rowIndex;
      const focusedColKey = event.api
        .getFocusedCell()
        ?.column.getColDef().field;
      if (rowIndex && focusedColKey) {
        event.api.startEditingCell({
          rowIndex,
          colKey: focusedColKey,
        });
      }
    }
    this.inlineEditingInProgress = true;
    event.context?.isInlineEditingInProgress$.next(true);
  }

  @HostListener('rowEditingStopped', ['$event'])
  rowEditingStopped(evt: Event): void {
    const event = evt as unknown as RowEditingStartedEvent;
    const { formGroup } = event.context as GridContext;
    this.inlineEditingInProgress = false;
    event.context.isInlineEditingInProgress$.next(false);

    const dirtyValues = DvFormUtils.getDirtyValues(formGroup!);
    const changedColumns = event.api
      .getColumns()
      ?.filter((col: Column) =>
        Object.keys(dirtyValues).includes(col.getColDef().field || '')
      );

    const upsert$ = this.getUpsert$(event.node, dirtyValues);

    if (upsert$ && changedColumns?.length && formGroup?.valid) {
      upsert$
        .pipe(
          catchError((resp: { error: HttpErrorModel }) => {
            event.context.shouldStopValueChanged = true;
            const initRowData = resp.error.InitialRowData;
            if (initRowData) {
              Object.keys(initRowData).forEach(key => {
                event.node.setDataValue(key, initRowData[key]);
              });
            }
            // wait until ag-grid emits onCellValueChanged event
            setTimeout(() => {
              event.context.shouldStopValueChanged = false;
            });
            return of();
          })
        )
        .subscribe(rowDataPatch => {
          event.node.setData({
            ...event.node.data,
            ...dirtyValues,
            ...rowDataPatch,
          });
          event.api.flashCells({
            rowNodes: [event.node!],
            columns: changedColumns,
          });
          this.inlineEditUpdateEnd.emit(this.agGrid.api);
        });
      this.enableFormGroup(event.context);
    }
  }

  tabToNextCell = (params: TabToNextCellParams): CellPosition => {
    const formGroup = (this.agGrid.gridOptions!.context as GridContext)
      .formGroup!;

    const { api } = params;
    const { nextCellPosition, previousCellPosition } = params;

    if (
      nextCellPosition?.rowIndex !== previousCellPosition.rowIndex ||
      nextCellPosition.rowPinned !== previousCellPosition.rowPinned
    ) {
      const columns = api.getAllDisplayedColumns();
      if (formGroup.invalid) {
        // invalid formControl name
        const [errorField] = Object.entries(formGroup.controls).find(
          ([_, formControl]) => formControl.invalid
        ) as [string, FormControl];

        const errorColumn =
          columns.find(
            (column: Column) => column.getColDef().field === errorField
          ) || previousCellPosition.column;

        return { ...previousCellPosition, column: errorColumn };
      } else {
        const prevIndex = previousCellPosition.rowIndex;
        const prevNode = api.getDisplayedRowAtIndex(prevIndex);

        // stop editing if rowIndex changes to complete row-editing logic.
        // fixes formValue updated beefore editingStopped event
        api.stopEditing();
        // when grid is done editing (rowEditingStopped)update focus
        setTimeout(() => {
          const nextColumn = params.backwards
            ? [...columns].reverse().find(c => c.isCellEditable(prevNode!))
            : columns.find(c => c.isCellEditable(prevNode!));
          const index = nextCellPosition?.rowIndex || prevIndex;

          api.clearRangeSelection();
          if (nextColumn) {
            api.ensureColumnVisible(nextColumn);
            api.setFocusedCell(index, nextColumn);
          }
        });

        return { ...nextCellPosition!, rowIndex: prevIndex };
      }
    }

    return nextCellPosition;
  };

  private getUpsert$(
    node: IRowNode,
    dirtyValues: Record<string, unknown>
  ): Observable<object | void> | undefined {
    if (this.upsertFn) {
      return this.upsertFn(node, dirtyValues);
    }
    if (this.entity && this.entityId) {
      return this.api
        .upsert(this.entity, this.entityId, dirtyValues)
        .pipe(map(() => undefined));
    }
    return undefined;
  }

  private enableFormGroup(context: GridContext): void {
    const { formGroup } = context;
    formGroup?.reset();
    formGroup?.enable({ emitEvent: false });
    formGroup?.markAsPristine();
  }
}
